const PROGRAM_UPDATE_STORAGE_KEY = 'vp_latest_program_update';

export function parseSeconds(value) {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    const splitParsed = parseInt((value || '').split('_')[0], 10);
    return Number.isNaN(splitParsed) ? 0 : splitParsed;
  }
  return 0;
}

export function normalizeScheduleType(value) {
  return (value || '').toString().trim().toLowerCase() === 'weekly' ? 'weekly' : 'daily';
}

export function formatExerciseScheduleLabel(exercise = {}, options = {}) {
  const frequencyType = normalizeScheduleType(exercise.frequencyType);
  const frequency = toNumber(exercise.frequency, 1);
  const useTitleCase = options.titleCase !== false;
  const typeLabel = frequencyType === 'weekly'
    ? (useTitleCase ? 'Weekly' : 'weekly')
    : (useTitleCase ? 'Daily' : 'daily');

  if (frequencyType === 'weekly') {
    const daysInbetween = getOptionalDaysInbetween(exercise);
    if (daysInbetween) {
      return `${frequency} ${typeLabel} with ${daysInbetween} days between sessions`;
    }
  }

  return `${frequency} ${typeLabel}`;
}

function wrapExerciseLabel(label) {
  return `[${label}]`;
}

export function buildExerciseEditParams(exercise = {}) {
  const frequencyType = normalizeScheduleType(exercise.frequencyType);
  return {
    sets: exercise.sets ?? 3,
    repetitions: exercise.repetitions ?? 10,
    repetitionMaximum: exercise.repetitionMaximum ?? 1,
    weightKg: exercise.weightKg ?? 0,
    weightTotalKg: exercise.weightTotalKg ?? 0,
    weightLbs: exercise.weightLbs ?? 0,
    weightTotalLbs: exercise.weightTotalLbs ?? 0,
    time: typeof exercise.time === 'string'
      ? parseInt(exercise.time, 10) || parseInt((exercise.time || '').split('_')[0], 10) || 30
      : exercise.time ?? 30,
    pause: typeof exercise.pause === 'string'
      ? parseInt(exercise.pause, 10) || parseInt((exercise.pause || '').split('_')[0], 10) || 15
      : exercise.pause ?? 15,
    frequencyType,
    frequency: exercise.frequency ?? 1,
    daysInbetween: frequencyType === 'weekly'
      ? (exercise.daysInbetween ?? exercise.daysInBetween ?? exercise.frequency ?? 1)
      : '',
    description: exercise.description ?? '',
    title: exercise.title ?? '',
    doseType: exercise.doseType || ((exercise.time && exercise.time !== 0) ? 'time' : 'repetitions'),
    img: exercise.img ?? exercise.picture ?? exercise.pictureURL ?? exercise.image_url ?? '',
    video: exercise.video ?? exercise.videoURL ?? exercise.video_url ?? '',
    patientInMedia: typeof exercise.patientInMedia === 'boolean' ? exercise.patientInMedia : null,
  };
}

export function normalizeEditedExerciseParams(params = {}) {
  const frequencyType = normalizeScheduleType(params.frequencyType || 'daily');
  const normalizedDaysInbetween = frequencyType === 'weekly'
    ? Math.max(1, toNumber(params.daysInbetween, toNumber(params.frequency, 1) || 1))
    : null;
  const normalizedImg = normalizeString(params.img || params.picture || params.image_url || '') || null;
  const normalizedVideo = normalizeString(params.video || params.video_url || '') || null;

  const doseType = normalizeDoseType(params.doseType);

  if (doseType === 'repetitions') {
    return {
      ...params,
      doseType,
      time: 0,
      repetitionMaximum: 0,
      weightKg: 0,
      weightTotalKg: 0,
      weightLbs: 0,
      weightTotalLbs: 0,
      daysInbetween: normalizedDaysInbetween,
      img: normalizedImg,
      video: normalizedVideo,
      patientInMedia: typeof params.patientInMedia === 'boolean' ? params.patientInMedia : null,
    };
  }
  if (doseType === 'repetition_maximum') {
    return {
      ...params,
      doseType,
      repetitions: 0,
      time: 0,
      repetitionMaximum: Math.max(1, toNumber(params.repetitionMaximum, 1)),
      weightKg: 0,
      weightTotalKg: 0,
      weightLbs: 0,
      weightTotalLbs: 0,
      daysInbetween: normalizedDaysInbetween,
      img: normalizedImg,
      video: normalizedVideo,
      patientInMedia: typeof params.patientInMedia === 'boolean' ? params.patientInMedia : null,
    };
  }
  if (isWeightDoseType(doseType)) {
    const weightField = getDoseFieldName(doseType);
    return {
      ...params,
      doseType,
      repetitions: 0,
      time: 0,
      repetitionMaximum: 0,
      weightKg: 0,
      weightTotalKg: 0,
      weightLbs: 0,
      weightTotalLbs: 0,
      [weightField]: Math.max(0, toNumber(params[weightField], 0)),
      daysInbetween: normalizedDaysInbetween,
      img: normalizedImg,
      video: normalizedVideo,
      patientInMedia: typeof params.patientInMedia === 'boolean' ? params.patientInMedia : null,
    };
  }
  return {
    ...params,
    doseType,
    repetitions: 0,
    repetitionMaximum: 0,
    weightKg: 0,
    weightTotalKg: 0,
    weightLbs: 0,
    weightTotalLbs: 0,
    daysInbetween: normalizedDaysInbetween,
    img: normalizedImg,
    video: normalizedVideo,
    patientInMedia: typeof params.patientInMedia === 'boolean' ? params.patientInMedia : null,
  };
}

function normalizeString(value) {
  return (value || '').toString().trim();
}

function getExerciseStableKey(exercise = {}, index = 0) {
  return exercise.id || exercise.uniqueKey || `${exercise.identifier || exercise.title || 'exercise'}::${index}`;
}

function getExerciseLabel(exercise = {}) {
  return normalizeString(exercise.title) || normalizeString(exercise.identifier) || 'Exercise';
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeDoseType(value) {
  const normalized = normalizeString(value || 'time').toLowerCase();
  if (normalized === 'repetitions' || normalized === 'repetition_maximum' || isWeightDoseType(normalized)) {
    return normalized;
  }
  return 'time';
}

function getDoseFieldName(doseType) {
  if (doseType === 'repetitions') return 'repetitions';
  if (doseType === 'repetition_maximum') return 'repetitionMaximum';
  if (doseType === 'weight_kg') return 'weightKg';
  if (doseType === 'weight_total_kg') return 'weightTotalKg';
  if (doseType === 'weight_lbs') return 'weightLbs';
  if (doseType === 'weight_total_lbs') return 'weightTotalLbs';
  return 'time';
}

function isWeightDoseType(doseType) {
  return ['weight_kg', 'weight_total_kg', 'weight_lbs', 'weight_total_lbs'].includes(doseType);
}

function formatDoseValue(exercise = {}, doseType = normalizeDoseType(exercise.doseType)) {
  if (doseType === 'repetitions') {
    return `${toNumber(exercise.repetitions)} repetitions`;
  }
  if (doseType === 'repetition_maximum') {
    return `${toNumber(exercise.repetitionMaximum)} RM`;
  }
  if (doseType === 'weight_kg') return `${toNumber(exercise.weightKg)} kg`;
  if (doseType === 'weight_total_kg') return `${toNumber(exercise.weightTotalKg)} total kg`;
  if (doseType === 'weight_lbs') return `${toNumber(exercise.weightLbs)} lbs`;
  if (doseType === 'weight_total_lbs') return `${toNumber(exercise.weightTotalLbs)} total lbs`;
  return `${parseSeconds(exercise.time)}s`;
}

function getOptionalDaysInbetween(exercise = {}) {
  const frequencyType = normalizeScheduleType(exercise.frequencyType || 'daily');
  if (frequencyType !== 'weekly') {
    return null;
  }
  const rawValue = exercise.daysInbetween ?? exercise.daysInBetween ?? null;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getPersistedDaysInbetween(exercise = {}) {
  const frequencyType = normalizeScheduleType(exercise.frequencyType || 'daily');
  if (frequencyType !== 'weekly') {
    return null;
  }

  const explicitDays = getOptionalDaysInbetween(exercise);
  if (explicitDays != null) {
    return explicitDays;
  }

  const fallbackFrequency = Number(exercise.frequency);
  return Number.isFinite(fallbackFrequency) && fallbackFrequency > 0 ? fallbackFrequency : 1;
}

function formatScheduleValue(exercise = {}) {
  return formatExerciseScheduleLabel(exercise, { titleCase: false });
}

export function formatExercisePrescriptionSummary(exercise = {}) {
  const doseType = normalizeDoseType(
    exercise.doseType || ((toNumber(exercise.repetitions) > 0 && parseSeconds(exercise.time) === 0) ? 'repetitions' : 'time')
  );
  const doseLabel = doseType === 'repetitions'
    ? `${toNumber(exercise.repetitions)} reps`
    : doseType === 'repetition_maximum'
      ? `${toNumber(exercise.repetitionMaximum)} RM`
      : isWeightDoseType(doseType)
        ? formatDoseValue(exercise, doseType)
    : `${parseSeconds(exercise.time)}s`;

  return `• ${toNumber(exercise.sets)} sets • ${doseLabel} • pause ${parseSeconds(exercise.pause)}s • ${formatExerciseScheduleLabel(exercise)}`;
}

export function formatAddedExerciseProgramJournalLine(exercise = {}) {
  return `Added ${wrapExerciseLabel(getExerciseLabel(exercise))} ${formatExercisePrescriptionSummary(exercise)} to the exercise program.`;
}

export function formatReplacedExerciseProgramJournalLine(fromLabel, nextExercise = {}) {
  return `Replaced ${wrapExerciseLabel(normalizeString(fromLabel) || 'Exercise')} with ${wrapExerciseLabel(getExerciseLabel(nextExercise))} ${formatExercisePrescriptionSummary(nextExercise)}.`;
}

function buildExerciseFieldChanges(originalExercise, nextExercise) {
  const changes = [];
  const originalDoseType = normalizeDoseType(originalExercise.doseType);
  const nextDoseType = normalizeDoseType(nextExercise.doseType);
  const originalDoseField = getDoseFieldName(originalDoseType);
  const nextDoseField = getDoseFieldName(nextDoseType);

  if (toNumber(originalExercise.sets) !== toNumber(nextExercise.sets)) {
    changes.push(`sets ${toNumber(originalExercise.sets)} to ${toNumber(nextExercise.sets)}`);
  }

  if (originalDoseType !== nextDoseType) {
    changes.push(`dose type ${originalDoseType} to ${nextDoseType}`);
  }

  const doseChanged = originalDoseType !== nextDoseType || (
    nextDoseField === 'repetitions'
      ? toNumber(originalExercise.repetitions) !== toNumber(nextExercise.repetitions)
      : nextDoseField === 'repetitionMaximum'
        ? toNumber(originalExercise.repetitionMaximum) !== toNumber(nextExercise.repetitionMaximum)
        : isWeightDoseType(nextDoseType)
          ? toNumber(originalExercise[nextDoseField]) !== toNumber(nextExercise[nextDoseField])
        : parseSeconds(originalExercise.time) !== parseSeconds(nextExercise.time)
  );

  if (doseChanged) {
    const doseLabel = nextDoseType === 'time' ? 'time' : 'dose';
    changes.push(`${doseLabel} ${formatDoseValue(originalExercise, originalDoseType)} to ${formatDoseValue(nextExercise, nextDoseType)}`);
  }

  if (parseSeconds(originalExercise.pause) !== parseSeconds(nextExercise.pause)) {
    changes.push(`pause ${parseSeconds(originalExercise.pause)}s to ${parseSeconds(nextExercise.pause)}s`);
  }

  const originalSchedule = formatScheduleValue(originalExercise);
  const nextSchedule = formatScheduleValue(nextExercise);
  if (originalSchedule !== nextSchedule) {
    changes.push(`schedule ${originalSchedule} to ${nextSchedule}`);
  }

  if (normalizeString(originalExercise.title) !== normalizeString(nextExercise.title) && normalizeString(originalExercise.identifier) === normalizeString(nextExercise.identifier)) {
    changes.push(`title updated to ${wrapExerciseLabel(getExerciseLabel(nextExercise))}`);
  }
  if (normalizeString(originalExercise.description) !== normalizeString(nextExercise.description)) {
    changes.push('description updated');
  }

  if (Boolean(originalExercise.is_paused) !== Boolean(nextExercise.is_paused)) {
    changes.push(nextExercise.is_paused ? 'paused' : 'resumed');
  }

  return changes;
}

export function summarizeExerciseProgramChanges({
  originalExercises = [],
  nextExercises = [],
} = {}) {
  const originalMap = new Map(originalExercises.map((exercise, index) => [getExerciseStableKey(exercise, index), exercise]));
  const nextMap = new Map(nextExercises.map((exercise, index) => [getExerciseStableKey(exercise, index), exercise]));
  const replacements = [];
  const updated = [];
  const removed = [];
  const added = [];
  const reordered = [];

  originalMap.forEach((originalExercise, stableKey) => {
    const nextExercise = nextMap.get(stableKey);
    if (!nextExercise) {
      removed.push({
        title: getExerciseLabel(originalExercise),
        identifier: normalizeString(originalExercise.identifier),
      });
      return;
    }

    if (normalizeString(originalExercise.identifier) !== normalizeString(nextExercise.identifier) && normalizeString(originalExercise.identifier || originalExercise.title) && normalizeString(nextExercise.identifier || nextExercise.title)) {
      replacements.push({
        from: getExerciseLabel(originalExercise),
        to: getExerciseLabel(nextExercise),
        fromIdentifier: normalizeString(originalExercise.identifier),
        toIdentifier: normalizeString(nextExercise.identifier),
        nextExercise,
      });
      return;
    }

    const fieldChanges = buildExerciseFieldChanges(originalExercise, nextExercise);
    if (fieldChanges.length > 0) {
      updated.push({
        title: getExerciseLabel(nextExercise),
        identifier: normalizeString(nextExercise.identifier),
        fieldChanges,
      });
    }
    // Index reorders are tracked separately so they trigger the
    // program-update event (and journal cache invalidation) without
    // polluting the journal note with a "reordered" line per exercise.
    const originalIndex = toNumber(originalExercise.index);
    const nextIndex = toNumber(nextExercise.index);
    if (originalIndex !== nextIndex) {
      reordered.push({
        title: getExerciseLabel(nextExercise),
        identifier: normalizeString(nextExercise.identifier),
        from: originalIndex,
        to: nextIndex,
      });
    }
  });

  nextMap.forEach((nextExercise, stableKey) => {
    if (!originalMap.has(stableKey)) {
      added.push({
        title: getExerciseLabel(nextExercise),
        identifier: normalizeString(nextExercise.identifier),
        exercise: nextExercise,
      });
    }
  });

  const journalLines = [
    ...replacements.map(change => formatReplacedExerciseProgramJournalLine(change.from, change.nextExercise || { title: change.to })),
    ...updated.map(change => `Updated ${wrapExerciseLabel(change.title)}: ${change.fieldChanges.join(', ')}.`),
    ...removed.map(change => `Removed ${wrapExerciseLabel(change.title)} from the exercise program.`),
    ...added.map(change => formatAddedExerciseProgramJournalLine(change.exercise || { title: change.title, identifier: change.identifier })),
  ];

  return {
    replacements,
    updated,
    removed,
    added,
    reordered,
    journalLines,
    // hasChanges intentionally includes reorders so the program-update
    // event still fires and downstream consumers (e.g. the journal view)
    // can invalidate their cached drafts to pick up the new index order.
    hasChanges: journalLines.length > 0 || reordered.length > 0,
  };
}

function emitExerciseProgramUpdate(detail) {
  if (typeof window === 'undefined') {
    return detail;
  }

  try {
    window.localStorage.setItem(PROGRAM_UPDATE_STORAGE_KEY, JSON.stringify(detail));
  } catch (error) {
    console.warn('Failed to persist latest exercise program update', error);
  }

  try {
    window.dispatchEvent(new CustomEvent('vp-program-updated', { detail }));
  } catch (error) {
    console.warn('Failed to dispatch exercise program update event', error);
  }

  return detail;
}

export function readLatestExerciseProgramUpdate() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(PROGRAM_UPDATE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('Failed to read latest exercise program update', error);
    return null;
  }
}

export function clearLatestExerciseProgramUpdate(filter = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const current = readLatestExerciseProgramUpdate();
    if (!current) {
      return;
    }

    const matchesTreatment = !filter.treatmentId || String(current.treatmentId) === String(filter.treatmentId);
    const matchesPhase = !filter.phaseId || String(current.phaseId) === String(filter.phaseId);
    const matchesSession = !filter.sessionId || String(current.sessionId || '') === String(filter.sessionId);

    if (matchesTreatment && matchesPhase && matchesSession) {
      window.localStorage.removeItem(PROGRAM_UPDATE_STORAGE_KEY);
    }
  } catch (error) {
    console.warn('Failed to clear latest exercise program update', error);
  }
}

export function confirmExerciseProgramUpdate(detail) {
  if (!detail) {
    return null;
  }

  return emitExerciseProgramUpdate({
    ...detail,
    journalConfirmed: true,
    journalConfirmedAt: Date.now(),
  });
}

export function reviewExerciseProgramUpdate(detail) {
  if (!detail) {
    return null;
  }

  return emitExerciseProgramUpdate({
    ...detail,
    ts: Date.now(),
    reviewInJournal: true,
    reviewRequestedAt: Date.now(),
  });
}

async function patchPatientExercises({
  userId,
  treatmentId,
  token,
  body,
}) {
  const response = await fetch(`https://api.virtus-one.com/treatment/exercise/edit/${userId}/${treatmentId}/`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Token ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to persist exercise changes');
  }

  return response.json().catch(() => null);
}

export async function persistExerciseProgramChanges({
  originalExercises = [],
  nextExercises = [],
  userId,
  treatmentId,
  phaseId,
  token,
  changeSource = 'program',
  sessionId = null,
}) {
  if (!userId || !treatmentId || !phaseId || !token) {
    throw new Error('Missing exercise persistence context');
  }

  const normalizedNextExercises = nextExercises.map(normalizeEditedExerciseParams);
  const changeSummary = summarizeExerciseProgramChanges({
    originalExercises,
    nextExercises: normalizedNextExercises,
  });

  const originalExerciseIds = originalExercises.filter(exercise => exercise?.id).map(exercise => exercise.id);
  const exercisesToRemove = originalExerciseIds.filter(
    id => !normalizedNextExercises.some(exercise => exercise.id === id)
  );
  const exercisesToAdd = normalizedNextExercises.filter(exercise => !exercise.id);
  const exercisesToUpdate = normalizedNextExercises.filter(exercise => exercise.id);

  if (exercisesToRemove.length > 0) {
    await patchPatientExercises({
      userId,
      treatmentId,
      token,
      body: {
        action: 'bulk_remove',
        phase_id: phaseId,
        exercise_ids: exercisesToRemove,
      },
    });
  }

  if (exercisesToAdd.length > 0) {
    await patchPatientExercises({
      userId,
      treatmentId,
      token,
      body: {
        action: 'bulk_add',
        phase_id: phaseId,
        exercises: exercisesToAdd.map(exercise => ({
          identifier: exercise.isExternal ? undefined : exercise.identifier,
          source: exercise.isExternal ? exercise.source : undefined,
          sourceExerciseId: exercise.isExternal ? exercise.sourceExerciseId : undefined,
          index: exercise.index,
          sets: exercise.sets,
          repetitions: exercise.repetitions,
          repetitionMaximum: exercise.repetitionMaximum,
          weightKg: exercise.weightKg,
          weightTotalKg: exercise.weightTotalKg,
          weightLbs: exercise.weightLbs,
          weightTotalLbs: exercise.weightTotalLbs,
          time: parseSeconds(exercise.time),
          doseType: normalizeDoseType(exercise.doseType),
          pause: parseSeconds(exercise.pause),
          frequencyType: exercise.frequencyType || 'daily',
          frequency: exercise.frequency || 1,
          daysInbetween: getPersistedDaysInbetween(exercise),
          description: exercise.description || '',
          title: exercise.title || '',
          pictureURL: exercise.img || exercise.picture || null,
          videoURL: exercise.video || exercise.video_url || null,
        })),
      },
    });
  }

  if (exercisesToUpdate.length > 0) {
    await patchPatientExercises({
      userId,
      treatmentId,
      token,
      body: {
        action: 'bulk_update',
        phase_id: phaseId,
        exercises: exercisesToUpdate.map(exercise => ({
          id: exercise.id,
          index: exercise.index,
          sets: exercise.sets,
          repetitions: exercise.repetitions,
          repetitionMaximum: exercise.repetitionMaximum,
          weightKg: exercise.weightKg,
          weightTotalKg: exercise.weightTotalKg,
          weightLbs: exercise.weightLbs,
          weightTotalLbs: exercise.weightTotalLbs,
          time: parseSeconds(exercise.time),
          doseType: normalizeDoseType(exercise.doseType),
          pause: parseSeconds(exercise.pause),
          frequency: exercise.frequency,
          frequencyType: exercise.frequencyType,
          daysInbetween: getPersistedDaysInbetween(exercise),
          description: exercise.description || '',
          title: exercise.title || '',
          picture: exercise.img || exercise.picture || null,
          video: exercise.video || exercise.video_url || null,
          is_paused: Boolean(exercise.is_paused),
        })),
      },
    });
  }

  const changeEvent = changeSummary.hasChanges
    ? emitExerciseProgramUpdate({
        ts: Date.now(),
        userId,
        treatmentId,
        phaseId,
        sessionId,
        source: changeSource,
        changeSummary,
      })
    : null;

  return {
    removed: exercisesToRemove.length,
    added: exercisesToAdd.length,
    updated: exercisesToUpdate.length,
    changeSummary,
    changeEvent,
  };
}